import React from 'react';
import { Page, Text, View, Document, StyleSheet, PDFViewer, PDFDownloadLink, Font, Image } from '@react-pdf/renderer';
import { BrowserView, MobileView } from 'react-device-detect';
import MobilePdfView from 'ui-component/MobilePdfView';
import { Stack, Typography } from '@mui/material';
import logo from 'assets/images/Logo-Aone-Final-Full.png';
import moment from 'moment';
import { ToWords } from 'to-words';

Font.register({ family: 'Noto Sans Lao', src: 'https://cdn.jsdelivr.net/gh/googlefonts/noto-fonts@master/hinted/ttf/NotoSansLao/NotoSansLao-Regular.ttf' });

const toWords = new ToWords({
    localeCode: 'en-US',
    converterOptions: { currency: false, ignoreDecimal: false, ignoreZeroCurrency: false, doNotAddOnly: false }
});

const formatCurrency = (num) => {
    if (num === undefined || num === null) return '0';
    return Number(num).toLocaleString('en-US');
};

const styles = StyleSheet.create({
    body: { paddingTop: 30, paddingBottom: 60, paddingHorizontal: 35, fontFamily: 'Noto Sans Lao' },
    title: { padding: 5, fontSize: 20, textAlign: 'center', fontWeight: 'bold' },
    section: { flexDirection: 'row', alignItems: 'center', fontSize: 9, marginBottom: 2 },
    label: { width: 85, fontWeight: 'bold' },
    labelRight: { width: 85, fontWeight: 'bold' },
    value: { flex: 1 },
    logo: { position: 'absolute', top: 20, left: 35, width: 80 },
    table: { width: '100%', marginTop: 10 },
    rowTable: { flexDirection: 'row', fontSize: 9 },
    headerTable: { backgroundColor: '#f8f9fa', fontWeight: 'bold', borderTopWidth: 1, borderTopColor: '#000', borderTopStyle: 'solid' },
    cell: { borderLeftWidth: 1, borderLeftColor: '#000', borderLeftStyle: 'solid', borderBottomWidth: 1, borderBottomColor: '#000', borderBottomStyle: 'solid', padding: 4 },
    lastCell: { borderRightWidth: 1, borderRightColor: '#000', borderRightStyle: 'solid' },
    colNo: { width: '8%' },
    colDesc: { width: '42%' },
    colQty: { width: '12%' },
    colPrice: { width: '18%' },
    colTotal: { width: '20%' },
    footerContainer: { marginTop: 10, borderTopWidth: 1, borderColor: '#000' },
    footerRow: { flexDirection: 'row', alignItems: 'stretch' },
    wordsBox: { width: '60%', borderLeftWidth: 1, borderBottomWidth: 1, borderColor: '#000', padding: 6, fontSize: 8, textAlign: 'center', justifyContent: 'center' },
    sideLabel: { width: '15%', borderLeftWidth: 1, borderBottomWidth: 1, borderColor: '#000', padding: 6, fontSize: 9, fontWeight: 'bold', justifyContent: 'center' },
    sideValue: { width: '25%', borderLeftWidth: 1, borderRightWidth: 1, borderBottomWidth: 1, borderColor: '#000', padding: 6, fontSize: 9, textAlign: 'right', justifyContent: 'center' },
    remarkLabel: { width: '15%', borderLeftWidth: 1, borderBottomWidth: 1, borderColor: '#000', padding: 6, fontWeight: 'bold', fontSize: 9, justifyContent: 'center' },
    remarkContent: { width: '85%', borderLeftWidth: 1, borderRightWidth: 1, borderBottomWidth: 1, borderColor: '#000', padding: 6, fontSize: 8, justifyContent: 'center' },
    grandTotalLabel: { width: '60%', borderLeftWidth: 1, borderBottomWidth: 1, borderColor: '#000', padding: 8, textAlign: 'center', fontWeight: 'bold', fontSize: 10, backgroundColor: '#f8f9fa' },
    grandTotalValue: { width: '40%', borderLeftWidth: 1, borderRightWidth: 1, borderBottomWidth: 1, borderColor: '#000', padding: 8, textAlign: 'right', fontWeight: 'bold', fontSize: 10, backgroundColor: '#f8f9fa' }
});

const MyDocument = ({ data }) => (
    <Document>
        <Page size="A4" style={styles.body}>
            <Image style={styles.logo} src={logo} />
            <Text style={styles.title}>Credit Note</Text>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 15, marginBottom: 5 }}>
                <View style={{ width: '55%' }}>
                    <View style={styles.section}><Text style={styles.label}>Credit No.</Text><Text style={styles.value}>: {data?.creditNo}</Text></View>
                    <View style={styles.section}><Text style={styles.label}>Customer no.</Text><Text style={styles.value}>: {data?.customerID}</Text></View>
                    <View style={styles.section}><Text style={styles.label}>Mobile</Text><Text style={styles.value}>: {data?.customers?.mobile || 'N/A'}</Text></View>
                    <View style={styles.section}><Text style={styles.label}>Customer</Text><Text style={styles.value}>: {data?.customers?.customerName || 'N/A'}</Text></View>
                    <View style={styles.section}><Text style={styles.label}>Address</Text><Text style={styles.value}>: {`${data?.customers?.villages?.vill_name || ''}, ${data?.customers?.districts?.dr_name || ''}, ${data?.customers?.provinces?.pr_name || ''}`}</Text></View>
                    {data?.announcementNo && <View style={styles.section}><Text style={styles.label}>Announce No.</Text><Text style={styles.value}>: {data?.announcementNo}</Text></View>}
                </View>
                <View style={{ width: '40%' }}>
                    <View style={styles.section}><Text style={styles.labelRight}>Date</Text><Text style={styles.value}>: {moment(data?.createdAt).format('DD-MM-yyyy HH:mm')}</Text></View>
                    <View style={styles.section}><Text style={styles.labelRight}>Reference No.</Text><Text style={styles.value}>: {data?.referenceNo || '-'}</Text></View>
                    <View style={styles.section}><Text style={styles.labelRight}>Promo Type</Text><Text style={styles.value}>: {data?.pomotionType || '-'}</Text></View>
                    <View style={styles.section}><Text style={styles.labelRight}>Type</Text><Text style={styles.value}>: {data?.type || '-'}</Text></View>
                </View>
            </View>
            <View style={styles.table}>
                <View style={[styles.rowTable, styles.headerTable]} fixed>
                    <Text style={[styles.cell, styles.colNo]}>No.</Text>
                    <Text style={[styles.cell, styles.colDesc]}>Description</Text>
                    <Text style={[styles.cell, styles.colQty]}>Qty</Text>
                    <Text style={[styles.cell, styles.colPrice]}>Unit Price</Text>
                    <Text style={[styles.cell, styles.colTotal, styles.lastCell]}>Total(₭)</Text>
                </View>
                {data?.description?.map((row, i) => (
                    <View key={i} style={styles.rowTable} wrap={false}>
                        <Text style={[styles.cell, styles.colNo]}>{i + 1}</Text>
                        <Text style={[styles.cell, styles.colDesc]}>{row?.Description}</Text>
                        <Text style={[styles.cell, styles.colQty]}>{row?.Quantity}</Text>
                        <Text style={[styles.cell, styles.colPrice]}>{formatCurrency(row?.Price)}</Text>
                        <Text style={[styles.cell, styles.colTotal, styles.lastCell]}>{formatCurrency(row?.Price * row?.Quantity)}</Text>
                    </View>
                ))}
            </View>
            <View style={styles.footerContainer} wrap={false}>
                <View style={styles.footerRow}>
                    <View style={styles.wordsBox}><Text>{toWords.convert(data?.amount || 0)}</Text></View>
                    <View style={styles.sideLabel}><Text>Sub Total</Text></View>
                    <View style={styles.sideValue}><Text>{formatCurrency(data?.amount)}</Text></View>
                </View>
                <View style={styles.footerRow}>
                    <View style={styles.remarkLabel}><Text>Remark</Text></View>
                    <View style={styles.remarkContent}><Text>{data?.commentRequest || '-'}</Text></View>
                </View>
                <View style={styles.footerRow}>
                    <View style={styles.grandTotalLabel}><Text>Grand Total</Text></View>
                    <View style={styles.grandTotalValue}><Text>{formatCurrency(data?.amount)}</Text></View>
                </View>
            </View>
            <View style={{ marginTop: 25, flexDirection: 'row', justifyContent: 'space-between' }} wrap={false}>
                <View style={{ width: '48%', textAlign: 'left' }}>
                    <Text style={{ fontSize: 10, fontWeight: 'bold' }}>Request By: {data?.requestBy || '....................'}</Text>
                    <Text style={{ fontSize: 9, marginTop: 10 }}>Signature : .....................................................</Text>
                    <Text style={{ fontSize: 9, marginTop: 5 }}>Date : ............................................................</Text>
                </View>
                <View style={{ width: '48%', textAlign: 'left' }}>
                    <Text style={{ fontSize: 10, fontWeight: 'bold' }}>Approve By: {data?.ApproveBy || '....................'}</Text>
                    <Text style={{ fontSize: 9, marginTop: 10 }}>Signature : .....................................................</Text>
                    <Text style={{ fontSize: 9, marginTop: 5 }}>Date : ............................................................</Text>
                </View>
            </View>
        </Page>
    </Document>
);

const CreditNoteInvoicePDF = ({ data }) => {
    if (!data) return null;
    return (
        <>
            <BrowserView style={{ width: '100%' }}>
                <PDFViewer style={{ width: '100%', height: '70vh' }}>
                    <MyDocument data={data} />
                </PDFViewer>
            </BrowserView>
            <MobileView style={{ width: '100%' }}>
                <MobilePdfView document={<MyDocument data={data} />} fileName={`CN_${data?.creditNo}`} />
            </MobileView>
        </>
    );
};

export default CreditNoteInvoicePDF;
